import { styled } from '@mui/material';

import { useState } from 'react';

 import { Typography } from '@app-velocity/ui';

import { useI18n } from '@app-velocity/i18n';
import { UnfoldLessIcon, UnfoldMoreIcon } from '@app-velocity/icons';

import { TAppraisalDto } from '@app-velocity/av-api-v2';

import { AppraisalElement } from '../AppraisalElement';
import { AppraisalSource } from '../AppraisalSource';
import { useAppraisalContext } from '../../../../context/useAppraisalContext';

export type AppraisalCategoryProps = {
  reviews: TAppraisalDto[];
};

const StyledAppraisalCategory = styled('div')<{variant?: 'dark' | 'light' }>(({ theme, variant }) => ({
  padding: theme.spacing(3),
  backgroundColor: variant === 'dark' ? 'var(--background-background-secondary-color)' : 'var(--background-background-color)',
  border: '1px solid var(--stroke-stroke-secondary-color)',
  borderRadius: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  cursor: 'pointer',
}));

const StyledAppraisalContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

const AppraisalHeader = styled('header')(({ theme }) => ({
  display: 'flex',
  aligItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}));

const Title = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontWeight: 500,
  color: 'var(--text-text-primary-color)',
}));

const Description = styled(Typography)({
  fontWeight: 300,
  fontSize: 13,
  lineHeight: 1.2,
  color: 'var(--text-text-secondary-color)',
});

const StyledExpandButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 12,
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: 'var(--text-text-primary-color)',
  svg: {
    fill: 'var(--text-text-primary-color)',
  },
  whiteSpace: 'nowrap',
});

const Cautions = styled('ul')(({ theme }) => ({
  margin: 0,
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexDirection: 'column',
  listStyleType: 'none',
}));

export const AppraisalGroup = ({ reviews }: AppraisalCategoryProps) => {
  const { t } = useI18n();
  const { variant } = useAppraisalContext();
  const [expanded, setExpanded] = useState(false);

  const toggle = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <StyledAppraisalCategory variant={variant}>
      <AppraisalHeader onClick={toggle} role="button">
        <Title>{reviews[0].rule}</Title>
        {expanded ? (
          <StyledExpandButton>
            {t('appraisals:appraisal_elements', { count: reviews.length })}
            <UnfoldLessIcon />
          </StyledExpandButton>
        ) : (
          <StyledExpandButton>
            {t('appraisals:appraisal_elements', { count: reviews.length })}
            <UnfoldMoreIcon />
          </StyledExpandButton>
        )}
      </AppraisalHeader>
      {expanded ? (
        <StyledAppraisalContent>
          <Description>{reviews[0].ruleDescription}</Description>
          <Cautions>
            {reviews.map((review) => (
              <AppraisalElement review={review} key={review.elementNodeId} />
            ))}
          </Cautions>
          <AppraisalSource review={reviews[0]} />
        </StyledAppraisalContent>
      ) : null}
    </StyledAppraisalCategory>
  );
};
